import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Volume2, CheckCircle, XCircle, ArrowRight, Play, Star, Lightbulb, ArrowLeft } from "lucide-react";
import lessons from "../data/lessons.json";
import PremiumGate, { isLessonFree, isPremium } from "../components/PremiumGate";
import { earnXP } from "../gamification";
import { playCorrect, playWrong } from "../sounds";
import DailySchedule from "../components/DailySchedule";
import { startActivityTracking, stopActivityTracking, markLessonCompleted } from "../lib/dailyPlan";
import SentenceBuilderGame from "../components/SentenceBuilderGame";
import LessonExamples from "../components/LessonExamples";
import VowelChart from "../components/VowelChart";
import { PATTERNS } from "../lib/sentenceGenerator";

const roleColors = {
  Subject: "from-sky to-ink-900",
  "Subject (Pronoun)": "from-sky to-ink-900",
  Verb: "from-sage to-sage-dark",
  Article: "from-gold-500 to-gold-600",
  "Object (Noun)": "from-gold-500 to-gold-600",
  Noun: "from-gold-500 to-gold-600",
  "Subject (Noun)": "from-sky to-ink-900",
  Preposition: "from-rust to-rust-dark",
  "Helping Verb": "from-sage to-sky-dark",
  "Helping Verb + Negative": "from-berry-light0 to-rust-dark",
};

export default function Lesson() {
  const { day } = useParams();
  const lesson = lessons.find((l) => l.day === Number(day));
  const [step, setStep] = useState(0); // 0=intro, 1=words, 2=builder, 3=complete
  const [showHint, setShowHint] = useState(false);

  // Sentence builder state
  const [selected, setSelected] = useState([]);
  const [builderResult, setBuilderResult] = useState(null);

  // Track time spent reading/practicing the lesson (counts toward 1hr written target)
  useEffect(() => {
    startActivityTracking("written", "lesson");
    return () => stopActivityTracking();
  }, []);

  // Mark daily lesson as completed when user reaches step 3 (complete screen)
  useEffect(() => {
    if (step === 3) {
      markLessonCompleted();
      window.dispatchEvent(new CustomEvent("lesson:complete"));
    }
  }, [step]);

  if (!lesson) {
    return (
      <div className="min-h-screen bg-paper text-ink-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-4">📭</div>
          <h2 className="text-2xl font-black">Lesson not found</h2>
          <Link to="/dashboard" className="text-paper mt-4 inline-block font-bold">← Back home</Link>
        </div>
      </div>
    );
  }

  if (!isLessonFree(Number(day)) && !isPremium()) {
    return <PremiumGate feature={`Lesson ${day}: ${lesson.topic}`} />;
  }

  // ===== PRACTICE LESSON (game day) =====
  if (lesson.type === "practice") {
    return <PracticeLesson lesson={lesson} day={day} />;
  }

  function speak(text) {
    if ("speechSynthesis" in window) {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = Number(localStorage.getItem("speechRate") || "0.85");
      u.lang = "en-US";
      window.speechSynthesis.speak(u);
    }
  }

  const totalSteps = 4;
  const progressPct = ((step + 1) / totalSteps) * 100;

  // Sentence builder words
  const sentenceWords = lesson.sentence.split(" ");
  const shuffledWords = [...sentenceWords].sort(() => Math.random() - 0.5);

  function checkSentence() {
    const isRight = selected.join(" ").toLowerCase() === lesson.sentence.toLowerCase();
    setBuilderResult(isRight);
    if (isRight) { playCorrect(); earnXP(20, "lesson"); }
    else { playWrong(); }
  }

  function resetBuilder() {
    setSelected([]);
    setBuilderResult(null);
  }

  return (
    <div className="min-h-screen bg-paper text-ink-900 flex flex-col full-bleed -mt-6">

      {/* TOP PROGRESS BAR */}
      <div className="p-4 flex items-center gap-4">
        <Link to="/dashboard" className="p-1.5 hover:bg-paper-deep rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex-1 h-3 bg-cream-200 rounded-full overflow-hidden">
          <div className="h-3 bg-gradient-to-r from-sage to-sage-dark rounded-full transition-all duration-700" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="text-sm font-bold text-ink-500">{step + 1}/{totalSteps}</span>
      </div>

      {/* DAILY 2-HOUR PLAN — compact summary */}
      <div className="px-4 pb-2">
        <DailySchedule compact />
      </div>

      {/* ===== STEP 0: INTRO ===== */}
      {step === 0 && (
        <div className="flex-1 flex flex-col justify-center items-center px-6 text-center page-enter relative">
          {/* Decorative blobs */}
          <div className="blob blob-pink" style={{ width: 320, height: 320, top: 20, left: -40, opacity: 0.35 }} />
          <div className="blob blob-cyan" style={{ width: 280, height: 280, bottom: 20, right: -30, opacity: 0.3 }} />

          <div className="chip-premium mb-3 bg-paper-deep border-cream-300 text-ink-900">
            Day {lesson.day} · {lesson.levelName}
          </div>
          <h1 className="text-3xl md:text-5xl font-black mb-5 relative">
            <span className="animate-text-shimmer">{lesson.topic}</span>
          </h1>

          {/* Main sentence */}
          <div className="glass-card-dark p-8 mb-6 max-w-md w-full cursor-pointer hover:scale-[1.02] transition-transform shine-once" onClick={() => speak(lesson.sentence)}>
            <p className="text-2xl md:text-3xl font-black text-center mb-3">"{lesson.sentence}"</p>
            <div className="flex items-center justify-center gap-2 text-ink-500 text-sm font-bold">
              <Play size={14} /> Tap to listen
            </div>
          </div>

          {/* Breakdown preview */}
          {lesson.breakdown && (
            <div className="flex flex-wrap gap-2 justify-center mb-6">
              {Object.entries(lesson.breakdown).map(([key, val]) => (
                <div key={key} className="bg-paper-deep border border-cream-300 rounded-xl px-3 py-1.5 text-center">
                  <p className="text-[10px] text-ink-500 uppercase font-bold">{key}</p>
                  <p className="font-bold text-sm">{val}</p>
                </div>
              ))}
            </div>
          )}

          {/* Vowel chart for article-related lessons */}
          {lesson.showVowelChart && (
            <div className="w-full max-w-lg mx-auto mb-6">
              <VowelChart />
            </div>
          )}

          {/* More examples carousel */}
          {lesson.examples && lesson.examples.length > 0 && (
            <div className="w-full max-w-lg mx-auto mb-6">
              <LessonExamples lesson={lesson} dark />
            </div>
          )}

          <button onClick={() => setStep(1)} className="btn-premium btn-premium-primary w-full max-w-md py-4 text-base">
            Learn Words <ArrowRight size={20} />
          </button>
        </div>
      )}

      {/* ===== STEP 1: WORD-BY-WORD ===== */}
      {step === 1 && (
        <div className="flex-1 flex flex-col px-4 sm:px-6 pt-4 pb-6 page-enter">
          <h2 className="text-xl font-black text-center mb-6">Word by Word Breakdown</h2>

          <div className="space-y-3 flex-1 overflow-y-auto max-w-lg mx-auto w-full">
            {lesson.words.map((w, i) => {
              const gradient = roleColors[w.role] || "from-gray-500 to-gray-600";
              return (
                <div key={i} className={`bg-paper-deep backdrop-blur border border-cream-300 rounded-2xl p-5 hover:bg-paper-deep transition-all cursor-pointer stagger-${Math.min(i+1,6)} fade-in`}
                  onClick={() => speak(w.word)}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`bg-gradient-to-r ${gradient} px-3 py-1 rounded-lg text-xs font-black shadow`}>{w.word}</div>
                      <span className={`text-[10px] font-bold bg-paper-deep px-2 py-0.5 rounded-full`}>{w.role}</span>
                    </div>
                    <Volume2 size={16} className="text-ink-400" />
                  </div>
                  <p className="text-sm text-ink-600">{w.meaning}</p>
                  <p className="text-xs text-ink-400 mt-1">💡 {w.why}</p>
                </div>
              );
            })}
          </div>

          {/* Article explanation */}
          {lesson.articleExplanation && (
            <div className="bg-gold-500/10 border border-gold-500/30 rounded-xl p-4 mt-4 max-w-lg mx-auto w-full">
              <p className="text-xs text-gold-300/80 font-semibold">📝 {lesson.articleExplanation}</p>
            </div>
          )}

          <button onClick={() => { setStep(2); resetBuilder(); }} className="w-full max-w-lg mx-auto mt-4 bg-gradient-to-r from-ink-900 to-gold-600 py-4 rounded-xl font-black text-lg shadow-lg hover:scale-[1.02] active:scale-[0.97] transition-all flex items-center justify-center gap-2">
            Practice Now <ArrowRight size={20} />
          </button>
        </div>
      )}

      {/* ===== STEP 2: SENTENCE BUILDER ===== */}
      {step === 2 && (
        <div className="flex-1 flex flex-col justify-center items-center px-6 text-center page-enter">
          <h2 className="text-2xl md:text-3xl font-black mb-2 text-ink-900">Arrange the sentence</h2>
          <p className="text-ink-500 text-sm mb-8">Tap words in the correct order</p>

          {/* Selected words */}
          <div className="flex flex-wrap gap-3 mb-8 justify-center min-h-[60px] bg-paper-deep border-2 border-dashed border-cream-300 rounded-2xl p-4 w-full max-w-md">
            {selected.length === 0 && <p className="text-ink-400 text-sm">Tap words below to build the sentence</p>}
            {selected.map((word, i) => (
              <button key={i} onClick={() => setSelected(selected.filter((_, idx) => idx !== i))}
                className="px-4 py-2 bg-sage/20 border-2 border-sage rounded-xl font-bold text-sm text-ink-900 hover:bg-sage/30 transition-all bounce-in">
                {word}
              </button>
            ))}
          </div>

          {/* Word options */}
          <div className="flex flex-wrap gap-3 justify-center mb-8">
            {shuffledWords.map((word, i) => {
              const isUsed = selected.includes(word) && selected.filter(w => w === word).length >= shuffledWords.filter(w => w === word).length;
              return (
                <button key={i} onClick={() => !isUsed && setSelected([...selected, word])}
                  disabled={isUsed}
                  className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
                    isUsed
                      ? "bg-paper border border-cream-200 text-ink-300"
                      : "bg-white border-2 border-cream-300 text-ink-900 hover:scale-105 hover:bg-paper-deep active:scale-95 shadow-sm"
                  }`}>
                  {word}
                </button>
              );
            })}
          </div>

          {/* Hint */}
          {!showHint ? (
            <button onClick={() => setShowHint(true)} className="text-gold-700 text-sm hover:text-gold-800 transition-colors flex items-center gap-1 mb-4 font-semibold">
              <Lightbulb size={14} /> Show Hint
            </button>
          ) : (
            <p className="text-gold-700 text-sm mb-4 bounce-in font-semibold">💡 {lesson.practice?.hint || `The correct sentence is: "${lesson.sentence}"`}</p>
          )}

          {/* Feedback */}
          {builderResult !== null && (
            <div className={`flex items-center gap-2 text-lg font-black mb-4 bounce-in ${builderResult ? "text-sage-dark" : "text-berry-dark"}`}>
              {builderResult ? <CheckCircle size={24} /> : <XCircle size={24} />}
              {builderResult ? "Correct! +20 XP 🎉" : "Not quite — try again!"}
            </div>
          )}

          {/* Actions */}
          <div className="w-full max-w-md space-y-3">
            {builderResult === null ? (
              <button onClick={checkSentence} disabled={selected.length === 0}
                className="w-full bg-gradient-to-r from-ink-900 to-gold-600 text-paper py-4 rounded-xl font-black text-lg shadow-lg hover:scale-[1.02] active:scale-[0.97] transition-all disabled:opacity-30">
                Check Answer
              </button>
            ) : builderResult ? (
              <button onClick={() => setStep(3)}
                className="w-full bg-gradient-to-r from-sage to-sage-dark text-paper py-4 rounded-xl font-black text-lg shadow-lg hover:scale-[1.02] active:scale-[0.97] transition-all flex items-center justify-center gap-2">
                Continue <ArrowRight size={20} />
              </button>
            ) : (
              <button onClick={resetBuilder}
                className="w-full bg-paper-deep border border-cream-300 text-ink-900 py-4 rounded-xl font-black text-lg hover:bg-cream-200 transition-all">
                Try Again
              </button>
            )}
          </div>
        </div>
      )}

      {/* ===== STEP 3: COMPLETE ===== */}
      {step === 3 && (
        <div className="flex-1 flex flex-col justify-center items-center px-6 text-center page-enter">
          <div className="text-6xl mb-4 bounce-in">🎉</div>
          <h2 className="text-3xl font-black mb-2">Lesson Complete!</h2>
          <p className="text-ink-500 mb-6">Day {lesson.day}: {lesson.topic}</p>

          {/* Stars */}
          <div className="flex gap-2 mb-6">
            {[1, 2, 3].map((s) => (
              <Star key={s} size={36} className="text-gold-700 fill-yellow-400 bounce-in" style={{ animationDelay: `${s * 150}ms` }} />
            ))}
          </div>

          {/* XP Card */}
          <div className="bg-paper-deep backdrop-blur border border-cream-300 rounded-2xl p-6 mb-8 max-w-sm w-full">
            <div className="flex justify-between items-center">
              <span className="text-ink-500 text-sm">XP Earned</span>
              <span className="text-2xl font-black gradient-text">+20 XP</span>
            </div>
          </div>

          {/* Actions — primary CTA pushes user to evening oral on the SAME topic */}
          <div className="w-full max-w-sm space-y-3">
            <Link to={`/oral/${lesson.day}`} className="block w-full bg-gradient-to-r from-sage via-sage-light0 to-sky-dark py-4 rounded-xl font-black text-base text-center shadow-2xl shadow-sage-light0/40 hover:scale-[1.02] active:scale-[0.97] transition-all relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              <span className="relative flex items-center justify-center gap-2">🌙 Now SPEAK this same topic →</span>
              <span className="relative block text-[10px] font-bold opacity-90 mt-0.5">Evening Oral · 1 hr · counts toward daily goal</span>
            </Link>
            <div className="grid grid-cols-2 gap-2">
              <Link to={`/practice/${lesson.day}`} className="block bg-paper-deep border border-cream-300 py-3 rounded-xl font-bold text-sm text-center hover:bg-cream-200 transition-all">
                Practice
              </Link>
              <Link to={`/test/${lesson.day}`} className="block bg-paper-deep border border-cream-300 py-3 rounded-xl font-bold text-sm text-center hover:bg-cream-200 transition-all">
                Test
              </Link>
            </div>
            {Number(day) < lessons.length && (
              <Link to={`/lesson/${Number(day) + 1}`} className="block w-full text-ink-500 py-2 text-center text-xs font-semibold hover:text-ink-700 transition-colors">
                Skip → Day {Number(day) + 1}
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// PRACTICE LESSON — game-only template (drag-drop sentence builder)
// ============================================================
function PracticeLesson({ lesson, day }) {
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);

  // Track time as written (game still teaches grammar visually)
  useEffect(() => {
    startActivityTracking("written", "practice-game");
    return () => stopActivityTracking();
  }, []);

  useEffect(() => {
    if (done) markLessonCompleted();
  }, [done]);

  const pattern = lesson.practicePattern || PATTERNS.SVO;

  if (done && result) {
    return (
      <div className="min-h-screen bg-paper text-ink-900 flex items-center justify-center full-bleed -mt-6 px-4">
        <div className="text-center max-w-md w-full space-y-5 page-enter">
          <div className="text-7xl animate-bounce">🏆</div>
          <h1 className="text-3xl font-black">Practice Complete!</h1>
          <p className="text-ink-600">Day {day}: {lesson.topic}</p>

          <div className="bg-paper-deep backdrop-blur-xl border border-cream-300 rounded-3xl p-6 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-ink-600">Total XP</span>
              <span className="text-3xl font-black text-gold-300">+{result.score}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-ink-600">Best combo</span>
              <span className="text-2xl font-black text-gold-300">🔥 {result.bestCombo}×</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-ink-600">Sentences built</span>
              <span className="text-xl font-black text-sage-light">{result.totalRounds}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Link to={`/oral/${day}`} className="block w-full bg-gradient-to-r from-sage to-sage-dark py-4 rounded-2xl font-black text-base shadow-2xl hover:scale-[1.02] transition">
              🌙 Now SPEAK this — Oral Class
            </Link>
            <Link to={`/lesson/${Number(day) + 1}`} className="block w-full bg-paper-deep border border-cream-300 py-3 rounded-2xl font-bold text-sm hover:bg-cream-200 transition">
              Next lesson — Day {Number(day) + 1} →
            </Link>
            <Link to="/dashboard" className="block w-full text-ink-500 py-2 text-xs font-bold hover:text-ink-700 transition">
              Back to dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper text-ink-900 full-bleed -mt-6 px-4 sm:px-6 lg:px-8 pt-4 pb-8">
      {/* Top bar */}
      <div className="flex items-center gap-3 mb-4 max-w-3xl mx-auto">
        <Link to="/dashboard" className="p-1.5 hover:bg-paper-deep rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-wider font-bold text-ink-500">🎮 Practice · Day {day}</p>
          <h1 className="font-black text-base">{lesson.topic}</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-1 mb-4">
        <DailySchedule compact />
      </div>

      <div className="bg-paper-deep backdrop-blur-xl border border-cream-300 rounded-3xl p-4 sm:p-5 max-w-3xl mx-auto">
        <p className="text-sm text-ink-700 text-center mb-4 italic">{lesson.articleExplanation}</p>
        <SentenceBuilderGame pattern={pattern} totalRounds={10} kid onComplete={(r) => { setResult(r); setDone(true); }} />
      </div>
    </div>
  );
}
